import * as React from 'react';

import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import type { Campaign_ProductType as ProductType } from '__generated__/graphql-types';

import { useLocation, useParams } from 'js/lib/useRouter';

import CampaignModulesDataContext from 'bundles/campaigns/context/CampaignModulesDataContext';
import GetCampaignModulesForXdpQuery from 'bundles/campaigns/queries/GetCampaignModulesForXdpQuery.graphql';
import type {
  GetCampaignModulesForXdpQuery as GetCampaignModulesForXdpQueryType,
  GetCampaignModulesForXdpQueryVariables,
} from 'bundles/campaigns/queries/__generated__/GetCampaignModulesForXdpQuery';
import shouldShowCampaigns from 'bundles/campaigns/utils/shouldShowCampaigns';

const CampaignModulesDataProviderForXdp = ({
  children,
  productType,
}: React.PropsWithChildren<{ productType: ProductType }>) => {
  const { query } = useLocation();
  const { productSlug } = useParams();
  const isPreview = query.preview === 'true' || query.preview === '1';
  const campaignId = query.campaignId ?? null;

  const { data, loading } = useQuery<GetCampaignModulesForXdpQueryType, GetCampaignModulesForXdpQueryVariables>(
    GetCampaignModulesForXdpQuery,
    {
      context: {
        clientName: 'gatewayGql',
      },
      variables: {
        campaignModulesVariables: {
          useContentfulPreview: isPreview,
          forceUseCampaignId: campaignId,
        },
        productType,
        slug: productSlug,
      },
      errorPolicy: 'all',
      ssr: true,
      skip: !shouldShowCampaigns(),
      onError: (error) => {
        Sentry.captureException(error, {
          extra: {
            message: 'Error fetching campaign modules on XDP',
            campaignId,
            isPreview,
            productType,
            productSlug,
          },
        });
      },
    }
  );

  let promoBarModuleData;

  const modules = data?.Campaign?.getCampaignModulesForXdp;
  if (modules?.promoBar?.__typename === 'Campaign_PromoBarModule') {
    promoBarModuleData = modules.promoBar.contentDataJson;
  }

  return (
    <CampaignModulesDataContext.Provider
      value={{
        loading,
        campaignData: {
          promoBarModule: {
            contentData: promoBarModuleData,
          },
        },
      }}
    >
      {children}
    </CampaignModulesDataContext.Provider>
  );
};

export default CampaignModulesDataProviderForXdp;
